import { useState } from 'react';
import type { QuotationDocumentData, QuotationDocumentItem } from '@/quotation/document/types';
import { getLabels } from '@/quotation/document/labels';
import type { QuotationLabels } from '@/quotation/document/labels';
import { formatQuantity, formatUnitPrice, formatMoney } from '@/quotation/document/formatters';

interface Props { data: QuotationDocumentData }

function ProductImage({ item, label, size }: {
  item: QuotationDocumentItem;
  label: string;
  size: 'lg' | 'sm';
}) {
  const [failed, setFailed] = useState(false);
  const boxCls = size === 'lg' ? 'w-24 h-24' : 'w-20 h-20';
  const iconCls = size === 'lg' ? 'text-2xl' : 'text-xl';

  if (failed || !item.productImageUrl) {
    return (
      <div className={`${boxCls} bg-neutral-50 border border-neutral-200 flex flex-col items-center justify-center gap-1 shrink-0`}>
        <i className={`ri-image-line text-neutral-300 ${iconCls}`} />
        <span className="text-xs text-neutral-400 text-center leading-tight px-1">{label}</span>
      </div>
    );
  }

  return (
    <img
      src={item.productImageUrl}
      alt={item.productName}
      className={`${boxCls} object-cover object-top bg-neutral-50 border border-neutral-200 shrink-0`}
      onError={() => setFailed(true)}
    />
  );
}

function buildMeta(item: QuotationDocumentItem, L: QuotationLabels) {
  const inline: { label: string; value: string }[] = [];
  if (item.materialDisplay) inline.push({ label: L.material, value: item.materialDisplay });
  if (item.capacity) inline.push({ label: L.capacity, value: item.capacity });
  if (item.specification) inline.push({ label: L.specification, value: item.specification });

  const category = item.categoryL1Display
    ? {
        label: L.category,
        value: [item.categoryL1Display, item.categoryL2Display].filter(Boolean).join(' / '),
      }
    : undefined;

  return { inline, category };
}

function MetaBlock({ inline, category }: {
  inline: { label: string; value: string }[];
  category?: { label: string; value: string };
}) {
  return (
    <div>
      {inline.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
          {inline.map((m, i) => (
            <div key={i} className="text-sm text-neutral-700 leading-snug">
              <span className="text-neutral-400">{m.label}: </span>
              {m.value}
            </div>
          ))}
        </div>
      )}
      {category && (
        <p className="mt-1 text-xs text-neutral-400 leading-snug">
          <span>{category.label}: </span>
          {category.value}
        </p>
      )}
    </div>
  );
}

interface ItemRowProps {
  item: QuotationDocumentItem;
  index: number;
  currency: string;
  locale: QuotationDocumentData['documentLocale'];
}

function ItemRow({ item, index, currency, locale }: ItemRowProps) {
  const L = getLabels(locale);
  const meta = buildMeta(item, L);

  return (
    <div className="py-6 border-b border-neutral-200 last:border-b-0">
      {/* Desktop layout — table-like row */}
      <div className="hidden sm:flex items-start gap-4">
        <ProductImage item={item} label={L.noImage} size="lg" />

        {/* Product + specification */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-semibold text-neutral-400 w-5 shrink-0 tabular-nums">
              {index + 1}.
            </span>
            <h3 className="text-base font-bold text-neutral-900 leading-snug">
              {item.productName}
            </h3>
          </div>
          {item.sku && (
            <p className="text-xs font-mono text-neutral-400 mt-1 ml-7">{L.sku}: {item.sku}</p>
          )}
          <div className="ml-7 mt-2.5">
            <MetaBlock inline={meta.inline} category={meta.category} />
          </div>
        </div>

        {/* Quantity */}
        <div className="w-[80px] text-right shrink-0">
          <div className="text-sm text-neutral-800 tabular-nums">
            {formatQuantity(item.quantity, locale)}
          </div>
        </div>

        {/* Unit price */}
        <div className="w-[110px] text-right shrink-0">
          <div className="text-sm text-neutral-800 tabular-nums">
            {formatUnitPrice(item.unitPrice, currency, locale)}
          </div>
        </div>

        {/* Amount */}
        <div className="w-[130px] text-right shrink-0">
          <div className="text-[15px] font-bold text-neutral-900 tabular-nums">
            {formatMoney(item.amount, currency, locale)}
          </div>
        </div>
      </div>

      {/* Mobile layout — stacked */}
      <div className="sm:hidden">
        <div className="flex items-start gap-3">
          <ProductImage item={item} label={L.noImage} size="sm" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-neutral-900 leading-snug">
              {item.productName}
            </h3>
            {item.sku && (
              <p className="text-xs font-mono text-neutral-400 mt-0.5">{L.sku}: {item.sku}</p>
            )}
          </div>
        </div>

        <div className="mt-2.5">
          <MetaBlock inline={meta.inline} category={meta.category} />
        </div>

        <div className="mt-3 space-y-1.5 border-t border-neutral-100 pt-2.5">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-400">{L.quantity}</span>
            <span className="text-neutral-800 font-medium tabular-nums">
              {formatQuantity(item.quantity, locale)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-400">{L.unitPrice}</span>
            <span className="text-neutral-800 font-medium tabular-nums">
              {formatUnitPrice(item.unitPrice, currency, locale)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-400">{L.amount}</span>
            <span className="text-neutral-900 font-bold tabular-nums">
              {formatMoney(item.amount, currency, locale)}
            </span>
          </div>
        </div>
      </div>

      {/* Finishings — subordinate commercial rows grouped with this product */}
      {item.finishing && item.finishing.length > 0 && (
        <div className="mt-3 sm:ml-28">
          <div className="bg-neutral-50/70 px-3 py-2.5">
            <p className="text-xs font-bold text-ink-700 uppercase tracking-wider mb-1.5">
              {L.finishing}
            </p>
            {item.finishing.map((f, fi) => (
              <div
                key={fi}
                className="flex items-baseline justify-between gap-4 py-1.5 border-b border-neutral-100 last:border-b-0"
              >
                <div className="flex-1 min-w-0 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="text-sm font-semibold text-neutral-800">
                    {f.displayName}
                  </span>
                  {(f.pantone || f.color) && (
                    <span className="text-xs text-neutral-400">{f.pantone || f.color}</span>
                  )}
                  {f.description && (
                    <span className="text-xs text-neutral-400">{f.description}</span>
                  )}
                </div>
                {f.amount != null && (
                  <span className="text-sm font-medium text-neutral-800 tabular-nums shrink-0">
                    {formatMoney(f.amount, currency, locale)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HtmlDocItems({ data }: Props) {
  const L = getLabels(data.documentLocale);

  return (
    <div className="px-8 md:px-12 mt-7">
      <p className="text-xs font-extrabold text-ink-700 uppercase tracking-widest mb-3">
        {L.product}
      </p>

      {/* Column header (desktop) */}
      <div className="hidden sm:flex items-center gap-4 border-b border-neutral-300 pb-2 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
        <div className="w-24 shrink-0" />
        <div className="flex-1 min-w-0">{L.product} / {L.specification}</div>
        <div className="w-[80px] text-right">{L.quantity}</div>
        <div className="w-[110px] text-right">{L.unitPrice}</div>
        <div className="w-[130px] text-right">{L.amount}</div>
      </div>

      <div>
        {data.items.map((item, i) => (
          <ItemRow
            key={item.id}
            item={item}
            index={i}
            currency={data.currency}
            locale={data.documentLocale}
          />
        ))}
      </div>
    </div>
  );
}